/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  LocalStorageService,
  PtCoreActions,
  PtCoreSelectors,
  UrlUtils,
} from '@pt/pt-core';
import {
  DeleteDependencies,
  LoggedUser,
  NotificationType,
  PtUiAppRoutes,
  UiPropagatedNotification,
} from '@pt/pt-models';
import { ErrorInterceptorService } from '@pt/pt-shared';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LogService } from '@pt/api-client';

@Injectable({
  providedIn: 'root',
})
export class ErrorInterceptor implements HttpInterceptor {
  private loginResponse$ = this.store.select(PtCoreSelectors.loginResponse);

  private ignored403UrlRegex = ['.*\\/api\\/.*\\/latest\\/widgets\\/'];
  private ignoredUrlRegex = [
    '.*\\/api\\/org\\/.*\\/re-open',
    '.*\\/api\\/org\\/.*\\/publish',
    '.*\\/api\\/org\\/dpias\\/.*\\/lock',
    '.*\\/api\\/org\\/treatments\\/.*\\/lock',
    '.*\\/api\\/org\\/ai-assessments\\/.*\\/lock',
    '.*\\/api\\/org\\/app-assessments\\/.*\\/lock',
    '.*\\/api\\/org\\/global-measure-checks\\/.*\\/lock',
    '.*\\/api\\/org\\/covid-assessments\\/.*\\/lock',
    '.*\\/api\\/org\\/v2\\/data-breach-processes\\/.*\\/lock',
    '.*\\/api\\/org\\/v2\\/data-breach-processes\\/incident-team\\/',

    // '.*\\/api\\/org\\/folders\\/.*\\/'
    '.*\\/api\\/org\\/users\\/forgot-password\\/.*',
    '.*\\/api\\/org\\/.*\\/.*\\/latest\\/is-validable',
    '.*\\/api\\/org\\/users\\/required-change-password.*',
    '.*\\/api\\/org\\/users\\/pw-validate',
    // '.*\\/api\\/org\\/templates\\/.*',

    // '.*\\/api\\/org\\/organizations\\/init',
    '.*\\/api\\/org\\/organizations\\/template-config\\/init',

    '.*\\/api\\/org\\/v2\\/users\\/forgot-password\\/.*',
    '.*\\/api\\/org\\/v2\\/users\\/required-change-password.*',
    '.*\\/api\\/org\\/v2\\/users\\/security-config/bom/upload',
    '.*\\/api\\/sec\\/v2\\/cyber-sec\\/search\\/.*',
  ];
  private logInRequestUrls = [
    '/api/adm/auth/login',
    '/api/org/auth/login',
    '/api/adm/auth/login-org',

    '/api/adm/v2/auth/login',
    '/api/org/v2/auth/login',
    '/api/adm/v2/auth/login-org'
  ];
  private logOutRequestUrls = ['/api/org/auth/logout'];
  private loggedUser?: LoggedUser;

  constructor(
    private store: Store,
    private log: LogService,
    private localStorageService: LocalStorageService,
    private errorInterceptorService: ErrorInterceptorService
  ) {
    this.loginResponse$
      .pipe(takeUntilDestroyed())
      .subscribe((loggedUser: LoggedUser) => {
        this.loggedUser = loggedUser;
      });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        switch (err.status) {
          case 401:
            return this.handle401Error(request, next, err);
          case 400:
            switch (true) {
              case !!err?.error?.reason:
                this.handle400Error(request, next, err?.error?.reason, true);
                break;
              case this.emailValidationRequest(request):
                this.handle400Error(
                  request,
                  next,
                  'MESSAGE.INVALID_EMAIL_NOTIFICATION',
                  false
                );
                break;
              case !!err?.error?.dependencies:
                this.handle400ErrorDependencies(request, next, err?.error);
                break;
              default:
                this.handle400Error(
                  request,
                  next,
                  'MESSAGE.ERROR_OCCURRED',
                  false
                );
                break;
            }
            return this.handleError(request, next, err);
          case 403: {
            let showErrorNotification = true;
            this.ignored403UrlRegex.forEach((regex) => {
              if (request.url.match(regex)) {
                showErrorNotification = false;
              }
            });
            if (showErrorNotification) {
              this.handle403Error();
            }
            break;
          }
          case 500:
            this.handle500Error(request, next, err);
            break;
          case 502:
            this.handle502Error(request, next, err);
            break;
          default:
            return this.handleError(request, next, err);
        }
      })
    );
  }
  private handle400ErrorDependencies(
    request: HttpRequest<any>,
    next: HttpHandler,
    notification: DeleteDependencies
  ) {
    let showErrorNotification = true;
    this.ignoredUrlRegex.forEach((regex) => {
      if (request.url.match(regex)) {
        showErrorNotification = false;
      }
    });

    if (showErrorNotification) {
      this.store.dispatch(
        PtCoreActions.propagateDependenciesNotification({ notification })
      );
    }
  }
  private handle400Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: string,
    translated: boolean
  ) {
    const notificationType = NotificationType.error;

    let showErrorNotification = true;
    this.ignoredUrlRegex.forEach((regex) => {
      if (request.url.match(regex)) {
        showErrorNotification = false;
      }
    });

    if (showErrorNotification) {
      const notification: UiPropagatedNotification = {
        type: notificationType,
        title: null,
        message: err,
        translated,
      };
      this.store.dispatch(
        PtCoreActions.propagateNotification({ notification: notification })
      );
    }
  }
  private handle403Error() {
    const notification: UiPropagatedNotification = {
      type: NotificationType.warning,
      title: null,
      message: 'MESSAGE.DETAILS_ERROR_403',
      translated: false,
    };
    this.store.dispatch(PtCoreActions.propagateNotification({ notification }));
  }
  private handle502Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse
  ) {
    this.errorInterceptorService.dispatchError(
      request.method,
      err.status,
      err.url
    );
  }
  private handle500Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse
  ) {
    let ignored = false;
    this.ignoredUrlRegex.forEach((regex) => {
      if (!request.url.match(regex)) {
        ignored = true;
      }
    });
    if (!ignored) {
      this.errorInterceptorService.dispatchError(
        request.method,
        err.status,
        err.url
      );
    }
  }

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: any
  ) {
    if (this.isNotLoginRequest(request) && this.isNotLogoutRequest(request)) {
      this.localStorageService.setLatestPage(window.location.href);
      this.store.dispatch(
        PtCoreActions.logoutAndClearData({
          route: [PtUiAppRoutes.LOGIN],
          domain: UrlUtils.getDomainIfExists(),
        })
      );
    }

    return throwError(() => err);
  }

  private handleError(
    request: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse
  ) {
    return throwError(() => err);
  }

  private emailValidationRequest(request: HttpRequest<any>) {
    return request.url.includes('email-validate');
  }

  private isNotLoginRequest(request: HttpRequest<any>) {
    for (const logInRequestUrl of this.logInRequestUrls) {
      if (request.url === logInRequestUrl) {
        return false;
      }
    }
    return true;
  }

  private isNotLogoutRequest(request: HttpRequest<any>) {
    for (const logOutRequestUrls of this.logOutRequestUrls) {
      if (request.url === logOutRequestUrls) {
        return false;
      }
    }
    return true;
  }
}
